<template>
  <v-card :loading="form.processing" flat>
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-form ref="form" class="mt-4" :disabled="isReadonly || form.processing">
        <v-row>
          <v-col cols="12" md="6">
            <div class="label mb-2">Name</div>
            <v-text-field
              v-model="form.name"
              :rules="[validationRules.required]"
              :error="form.errors.name"
              :error-messages="form.errors.name"
              solo
              flat
              outlined
              dense
              placeholder="Student full name"
            />
          </v-col>
          <v-col cols="12" :md="form.status === 'INACTIVE' ? 2 : 3">
            <div class="label mb-2">Registration Date</div>
            <v-menu
              ref="regisDateMenu"
              v-model="regisDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="form.registration_date"
                  placeholder="Select registration date"
                  prepend-icon="mdi-calendar"
                  solo
                  flat
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.registration_date"
                @change="$refs.regisDateMenu.save(form.registration_date)"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" :md="form.status === 'INACTIVE' ? 2 : 3">
            <div class="label mb-2">Status</div>
            <v-select
              v-model="form.status"
              :items="statusOptions"
              :rules="[validationRules.required]"
              :error="form.errors.status"
              :error-messages="form.errors.status"
              solo
              flat
              outlined
              dense
              placeholder="Select status"
              @change="changeStatus"
            />
          </v-col>
          <v-col v-if="form.status === 'INACTIVE'" cols="12" md="2">
            <div class="label mb-2">Inactive Date</div>
            <v-menu
              ref="inactiveDateMenu"
              v-model="inactiveDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="form.inactive_date"
                  placeholder="Select inactive date"
                  prepend-icon="mdi-calendar"
                  solo
                  flat
                  outlined
                  dense
                  readonly
                  :error="form.errors.inactive_date"
                  :error-messages="form.errors.inactive_date"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.inactive_date"
                @change="$refs.inactiveDateMenu.save(form.inactive_date)"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <div class="label mb-2">Phone Number</div>
            <v-text-field
              v-model="form.phone_number"
              solo
              flat
              outlined
              dense
              placeholder="Phone number"
            />
          </v-col>
          <v-col cols="12" md="3">
            <div class="label mb-2">Gender</div>
            <v-select
              v-model="form.gender"
              :items="genderOptions"
              :rules="[validationRules.required]"
              :error="form.errors.gender"
              :error-messages="form.errors.gender"
              solo
              flat
              outlined
              dense
              placeholder="Gender"
            />
          </v-col>
          <v-col cols="12" md="3">
            <div class="label mb-2">Date of Birth</div>
            <v-menu
              ref="dobMenu"
              v-model="dobMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="form.date_of_birth"
                  placeholder="Select birth date"
                  prepend-icon="mdi-calendar"
                  solo
                  flat
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.date_of_birth"
                @change="$refs.dobMenu.save(form.date_of_birth)"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12">
            <div class="label mb-2">Address</div>
            <v-textarea
              v-model="form.address"
              solo
              flat
              outlined
              dense
              auto-grow
              placeholder="Address"
            />
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="12" md="4">
            <div class="label mb-2">Email</div>
            <v-text-field
              v-model="form.email"
              :rules="[validationRules.required, validationRules.email]"
              :error="form.errors.email"
              :error-messages="form.errors.email"
              solo
              flat
              outlined
              dense
              placeholder="Email will be used for login"
            />
          </v-col>
          <v-col cols="12" md="4">
            <div class="label mb-2">Password</div>
            <v-text-field
              v-model="form.password"
              solo
              flat
              outlined
              dense
              :disabled="isGeneratePassword"
            />
          </v-col>
          <v-col v-if="mode == 'add'" cols="12" md="2">
            <v-row align="center" no-gutters>
              <v-col>
                <v-checkbox
                  v-model="isGeneratePassword"
                  class="mt-0"
                  hide-details
                  @change="generatePassword"
                >
                  <template #label class="">
                    <v-tooltip bottom>
                      <template #activator="{ attrs, on }">
                        <div v-bind="attrs" v-on="on">
                          Auto generate password?
                        </div>
                      </template>
                      Password will be set from birth date with format: yyyymmdd
                      (e.g. 19930524)
                    </v-tooltip>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Game Level</div>
            <v-select
              v-model="form.game_level"
              :items="gameLevelOptions"
              solo
              flat
              outlined
              dense
              placeholder="Select game level"
            />
          </v-col>
        </v-row>
        <v-card flat>
          <v-card-title class="px-0">Private Teachers</v-card-title>
          <v-divider></v-divider>
          <v-card-text class="px-0">
            <v-row align="center">
              <v-col cols="10" md="6">
                <v-autocomplete
                  v-model="selectedPrivateTeacher"
                  :items="availablePrivateTeachers"
                  :loading="isPrivateTeachersLoading"
                  :search-input.sync="searchInputPrivateTeacher"
                  return-object
                  item-text="name"
                  item-value="id"
                  solo
                  flat
                  outlined
                  dense
                  placeholder="Choose private teacher"
                  cache-items
                  hide-details
                />
              </v-col>
              <v-col cols="2" md="2">
                <v-btn
                  depressed
                  color="success"
                  :icon="$vuetify.breakpoint.mobile"
                  :disabled="availablePrivateTeachers.length === 0"
                  @click="addPrivateTeacher"
                >
                  <v-icon :left="!$vuetify.breakpoint.mobile">
                    mdi-plus
                  </v-icon>
                  <span v-if="!$vuetify.breakpoint.mobile"> Add Teacher </span>
                </v-btn>
              </v-col>
            </v-row>
            <v-list>
              <template v-for="(privateTeaching, i) in form.private_teachings">
                <v-list-item :key="`item-${i}`" :disabled="isReadonly">
                  <v-list-item-content>
                    <v-row align="center">
                      <v-col cols="10" md="6">
                        <v-list-item-title>
                          {{ privateTeaching.teacher.name }}
                        </v-list-item-title>
                      </v-col>
                      <v-col cols="2">
                        <v-btn
                          depressed
                          color="error"
                          small
                          :icon="$vuetify.breakpoint.mobile"
                          @click="removePrivateTeacher(i)"
                        >
                          <v-icon :left="!$vuetify.breakpoint.mobile">
                            mdi-close
                          </v-icon>
                          <span v-if="!$vuetify.breakpoint.mobile">
                            Remove
                          </span>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list>
          </v-card-text>
        </v-card>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col md="3" cols="12">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col md="3" cols="12">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isReadonly || form.processing"
              @click="submit"
              >Save Changes</v-btn
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import constant from '@/constant'
export default {
  name: 'StudentForm',
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    student: {
      type: Object,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      dobMenu: false,
      regisDateMenu: false,
      inactiveDateMenu: false,
      form: this.$inertia.form({
        name: null,
        email: null,
        phone_number: null,
        gender: null,
        date_of_birth: null,
        password: null,
        status: null,
        profile_image_url: null,
        address: null,
        game_level: null,
        registration_date: null,
        inactive_date: null,
        private_teachings: [],
      }),
      isGeneratePassword: false,
      selectedPrivateTeacher: null,
      searchInputPrivateTeacher: null,
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'student/isDetailLoading',
      privateTeachers: 'student/privateTeachers',
      isPrivateTeachersLoading: 'student/isPrivateTeachersLoading',
    }),
    isReadonly() {
      return this.mode === 'view'
    },
    genderOptions() {
      return constant.genderOptions
    },
    statusOptions() {
      return constant.studentStatusOptions
    },
    gameLevelOptions() {
      return constant.gameLevelOptions
    },
    availablePrivateTeachers() {
      if (this.form.private_teachings.length === 0) {
        return this.privateTeachers
      }
      const assignedIds = this.form.private_teachings.map((e) => e.teacher_id)
      return this.privateTeachers.filter((t) => !assignedIds.includes(t.id))
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
    searchInputPrivateTeacher(val) {
      if (val) {
        this.debouncedSearchPrivateTeachers()
      }
    },
  },
  created() {
    this.init()
    this.debouncedSearchPrivateTeachers = _.debounce(
      this.searchPrivateTeachers,
      500
    )
  },
  methods: {
    ...mapActions({
      create: 'student/create',
      update: 'student/update',
      upload: 'storage/upload',
      handleError: 'handleError',
      getPrivateTeachers: 'student/getPrivateTeachers',
    }),
    validateProps() {
      if ((this.mode === 'edit' || this.mode === 'view') && !this.student) {
        console.error(
          `student props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    init() {
      if (this.mode != 'add' && this.student != null) {
        _.merge(this.form, this.student)
      }
    },
    generatePassword() {
      this.form.password = this.form.date_of_birth
        ? this.form.date_of_birth.replaceAll('-', '')
        : null
    },
    changeStatus() {
      if (this.form.status === 'INACTIVE') {
        if (this.form.inactive_date == null) {
          this.form.inactive_date = this.formatDate(new Date())
        }
      } else {
        this.form.inactive_date = null
      }
    },
    async uploadFile({ file, prop }) {
      const responseData = await this.upload({ file, baseDir: 'student' })
      if (responseData) {
        this.form[prop] = responseData.url
      }
    },
    async searchPrivateTeachers() {
      await this.getPrivateTeachers({
        search: this.searchInputPrivateTeacher,
        filters: {
          status: 'ACTIVE',
        },
      })
    },
    addPrivateTeacher() {
      this.form.private_teachings.push({
        id: null,
        student_id: this.student?.id ?? null,
        teacher_id: this.selectedPrivateTeacher.id,
        teacher: { ...this.selectedPrivateTeacher },
      })
    },
    removePrivateTeacher(index) {
      this.form.private_teachings.splice(index, 1)
    },
    cancel() {
      this.$emit('cancel')
    },
    async submit() {
      if (this.$refs.form.validate()) {
        if (this.mode === 'add') {
          this.form.post('/students')
        } else if (this.mode === 'edit') {
          this.form.put(`/students/${this.student.id}`)
        }
      }
    },
  },
}
</script>
